import { useEffect, useState } from 'react';
import { authApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { C } from '../../theme/palette';
import { RelierApplication } from './RelierApplication.jsx';

/**
 * L'application d'authentification (Google Authenticator…) dans Sécurité :
 * la relier, la voir reliée, la retirer contre le mot de passe.
 *
 * <p>Reliée, c'est son code qui est demandé après le mot de passe ; rien ne
 * part par e-mail ni par SMS. Le code par e-mail reste un secours, pour un
 * téléphone oublié.
 *
 * @returns {JSX.Element | null} la carte, ou rien pour un compte sans adresse
 */
export function ApplicationCard() {
  const { session } = useSession();
  // `null` tant que le serveur n'a pas répondu.
  const [reliee, setReliee] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState('');
  const [info, setInfo] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const sansAdresse = !session || session.accesParPin;

  useEffect(() => {
    if (sansAdresse) return undefined;
    let annule = false;
    authApi
      .etatDoubleAuth()
      .then((etat) => {
        if (!annule) setReliee(Boolean(etat?.application));
      })
      .catch(() => {
        if (!annule) setReliee(false);
      });
    return () => {
      annule = true;
    };
  }, [sansAdresse]);

  if (sansAdresse) return null;

  const retire = async () => {
    setErreur('');
    setInfo('');
    setEnvoi(true);
    try {
      await authApi.retireApplication(motDePasse);
      setReliee(false);
      setMotDePasse('');
      setInfo(
        'Application retirée. Le code arrivera par e-mail si la double authentification reste active.',
      );
    } catch (e) {
      setErreur(e?.message || 'La demande n’a pas abouti.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="🔑"
        title="Application Authenticator"
        color={C.teal}
        right={
          <span style={{ fontSize: 9, fontWeight: 800, color: reliee ? C.green : C.muted }}>
            {reliee === null ? '…' : reliee ? 'Reliée' : 'Le plus sûr'}
          </span>
        }
      />
      <div style={{ padding: '13px 15px', fontSize: 12, lineHeight: 1.6, color: C.ink }}>
        {reliee === true && (
          <>
            <div style={{ marginBottom: 10 }}>
              ✅ <b>Reliée.</b> À chaque connexion, après ton mot de passe, SwissTech te demande le
              code affiché sous <b>SwissTech</b> dans ton application. Sans ton téléphone, tu peux
              recevoir un code par e-mail.
            </div>
            <Inp
              label="Mot de passe actuel, pour retirer l’application"
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
            />
            <Btn
              label={envoi ? 'Un instant…' : 'Retirer l’application'}
              color={C.red}
              ghost
              full
              disabled={envoi || !motDePasse}
              onClick={retire}
            />
          </>
        )}

        {reliee === false && !ouvert && (
          <>
            <div style={{ marginBottom: 10 }}>
              Google Authenticator, Microsoft Authenticator… Un code qui change toutes les 30
              secondes, même sans réseau. Personne ne peut l’intercepter.
            </div>
            <Btn
              label="Relier mon application"
              color={C.teal}
              full
              onClick={() => {
                setOuvert(true);
                setInfo('');
              }}
            />
          </>
        )}

        {reliee === false && ouvert && (
          <RelierApplication
            onRelie={() => {
              setReliee(true);
              setOuvert(false);
              setInfo('Application reliée : son code te sera demandé à chaque connexion.');
            }}
          />
        )}

        {reliee === null && <div style={{ color: C.muted }}>Lecture de l’état…</div>}

        {erreur && (
          <div role="alert" style={{ fontSize: 10, color: C.red, fontWeight: 700, marginTop: 8 }}>
            {erreur}
          </div>
        )}
        {info && (
          <div
            role="status"
            style={{ fontSize: 10, color: C.green, fontWeight: 700, marginTop: 8 }}
          >
            {info}
          </div>
        )}
      </div>
    </Card>
  );
}
